import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import ProfileReminder from "./ProfileReminder";
import { api } from "../lib/api";
import {
  House, Books, ListChecks, ChartBar, Users, GraduationCap,
  Buildings, Eye, SignOut, Certificate, Trophy, Wallet as WalletIcon, Gear, UserCircle, Chats, Lifebuoy, Ticket, Coin, ClipboardText, List, X,
} from "@phosphor-icons/react";

function WalletChip() {
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/wallet/me").then((r) => { if (!cancelled) setBalance(r.data.balance); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return (
    <Link
      to="/app/wallet"
      className="mx-3 flex items-center justify-between gap-2 px-3 py-2 bg-zinc-900 text-white hover:bg-zinc-800"
      data-testid="sidebar-wallet-chip"
    >
      <div className="inline-flex items-center gap-1.5">
        <Coin size={14} weight="fill" className="text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80">Wallet</span>
      </div>
      <span className="font-mono-tab text-sm font-bold" data-testid="sidebar-wallet-balance">
        {balance == null ? "…" : balance.toLocaleString()}
      </span>
    </Link>
  );
}

const ROLE_NAV = (t) => ({
  student: [
    { to: "/app", label: t("nav.dashboard"), icon: House },
    { to: "/app/exams", label: t("nav.exams"), icon: ListChecks },
    { to: "/app/results", label: t("nav.results"), icon: GraduationCap },
    { to: "/app/certificates", label: t("nav.certificates"), icon: Certificate },
    { to: "/app/leaderboard", label: t("nav.leaderboard"), icon: Trophy },
    { to: "/app/forum", label: "Forum", icon: Chats },
    { to: "/app/tickets", label: "Help desk", icon: Lifebuoy },
    { to: "/app/wallet", label: "Wallet", icon: WalletIcon },
    { to: "/app/profile", label: "Profile", icon: UserCircle },
  ],
  examiner: [
    { to: "/app", label: t("nav.dashboard"), icon: House },
    { to: "/app/questions", label: t("nav.question_bank"), icon: Books },
    { to: "/app/exams-manage", label: t("nav.exams"), icon: ListChecks },
    { to: "/app/review-requests", label: "Review requests", icon: ClipboardText },
    { to: "/app/forum", label: "Forum", icon: Chats },
    { to: "/app/tickets", label: "Help desk", icon: Lifebuoy },
    { to: "/app/analytics", label: t("nav.analytics"), icon: ChartBar },
    { to: "/app/profile", label: "Profile", icon: UserCircle },
  ],
  institution_admin: [
    { to: "/app", label: t("nav.dashboard"), icon: House },
    { to: "/app/institution", label: "Institution", icon: Buildings },
    { to: "/app/users", label: t("nav.people"), icon: Users },
    { to: "/app/questions", label: t("nav.question_bank"), icon: Books },
    { to: "/app/exams-manage", label: t("nav.exams"), icon: ListChecks },
    { to: "/app/proctor", label: t("nav.proctor"), icon: Eye },
    { to: "/app/review-requests", label: "Review requests", icon: ClipboardText },
    { to: "/app/coupons", label: "Coupons", icon: Ticket },
    { to: "/app/forum", label: "Forum", icon: Chats },
    { to: "/app/tickets", label: "Help desk", icon: Lifebuoy },
    { to: "/app/analytics", label: t("nav.analytics"), icon: ChartBar },
    { to: "/app/settings", label: t("nav.settings"), icon: Gear },
    { to: "/app/profile", label: "Profile", icon: UserCircle },
  ],
  super_admin: [
    { to: "/app", label: t("nav.overview"), icon: House },
    { to: "/app/institutions", label: t("nav.institutions"), icon: Buildings },
    { to: "/app/users", label: t("nav.all_users"), icon: Users },
    { to: "/app/review-requests", label: "Review requests", icon: ClipboardText },
    { to: "/app/coupons", label: "Coupons", icon: Ticket },
    { to: "/app/forum", label: "Forum", icon: Chats },
    { to: "/app/tickets", label: "Help desk", icon: Lifebuoy },
    { to: "/app/analytics", label: t("nav.analytics"), icon: ChartBar },
    { to: "/app/settings", label: t("nav.settings"), icon: Gear },
    { to: "/app/profile", label: "Profile", icon: UserCircle },
  ],
  proctor: [
    { to: "/app", label: t("nav.dashboard"), icon: House },
    { to: "/app/proctor", label: t("nav.live_monitor"), icon: Eye },
    { to: "/app/forum", label: "Forum", icon: Chats },
    { to: "/app/tickets", label: "Help desk", icon: Lifebuoy },
    { to: "/app/profile", label: "Profile", icon: UserCircle },
  ],
});

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = useMemo(
    () => (ROLE_NAV(t)[user?.role] || ROLE_NAV(t).student),
    [user?.role, i18n.language, t]
  );

  // Auto-close mobile drawer whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Lock body scroll when drawer open on mobile
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const roleLabel = {
    student: "Student",
    examiner: "Examiner",
    institution_admin: "Institution Admin",
    super_admin: "Super Admin",
    proctor: "Proctor",
  }[user?.role] || "User";

  return (
    <div className="min-h-screen flex bg-white text-zinc-900">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4" data-testid="mobile-topbar">
        <Link to="/app" className="font-display text-lg font-bold tracking-tight">
          EXAM<span className="text-blue-600">/</span>OS
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 -mr-2 text-zinc-700 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <List size={22} weight="regular" />
        </button>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-zinc-900/50"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 md:w-60 border-r border-zinc-200 bg-white flex flex-col overflow-y-auto transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="app-sidebar"
        aria-label="Primary navigation"
      >
        <div className="px-5 py-6 border-b border-zinc-200 flex items-center justify-between">
          <Link to="/app" className="block" data-testid="sidebar-logo-link">
            <div className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t("app.tagline")}</div>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-2 -mr-2 text-zinc-600 hover:bg-zinc-100"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X size={20} weight="regular" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" role="navigation" aria-label="Main">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm font-medium border-l-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  isActive
                    ? "border-zinc-900 bg-zinc-50 text-zinc-900"
                    : "border-transparent text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={18} weight="regular" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-200 space-y-3">
          <div className="px-3 py-2 flex items-center gap-3">
            <SidebarAvatar user={user} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("nav.signed_in_as")}</div>
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
              <div className="mt-1 inline-block text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-0.5">
                {roleLabel}
              </div>
            </div>
          </div>
          {user?.role === "student" && <WalletChip />}
          <div className="px-3"><LanguageSwitcher compact /></div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            data-testid="logout-button"
          >
            <SignOut size={16} aria-hidden="true" />
            {t("nav.sign_out")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main id="main-content" className="flex-1 min-w-0 bg-white pt-14 md:pt-0" data-testid="app-main" tabIndex={-1}>
        <div className="px-4 sm:px-8 lg:px-12 py-6 md:py-10 max-w-[1400px]">
          <ProfileReminder />
          {children}
        </div>
      </main>
    </div>
  );
}

export function PageHeader({ overline, title, subtitle, action }) {
  return (
    <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        {overline && (
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">{overline}</div>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}


function SidebarAvatar({ user }) {
  const [err, setErr] = React.useState(false);
  const src = React.useMemo(() => {
    if (!user) return "";
    const tok = localStorage.getItem("session_token") || "";
    return user.avatar_file_id
      ? `${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${user.avatar_file_id}?auth=${encodeURIComponent(tok)}`
      : (user.picture || "");
  }, [user]);
  React.useEffect(() => { setErr(false); }, [src]);
  if (!user) return null;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  if (!src || err) {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-zinc-900 text-white font-bold text-sm flex-shrink-0" data-testid="sidebar-avatar-placeholder">
        {initial}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="w-10 h-10 rounded-full object-cover border border-zinc-200 flex-shrink-0"
      data-testid="sidebar-avatar"
      onError={() => setErr(true)}
    />
  );
}
